import { z } from "zod";
import type { AddressKeyScheme } from "@remi/enums/address-key-scheme";

import { CreateHomeownerSchema, UpdateHomeownerSchema } from "./person";
import {
  type CreatePropertyAddress,
  CreatePropertyAddressSchema,
  UpdatePropertyAddressSchema,
} from "./propertyAddress";

export const SITE_ACCESS_FIELDS = ["gateCode", "gateCodeNeeded", "hoaApprovalNeeded", "additionalInformation"] as const;

export type WorksiteAddressRole = "property" | "billing";

export const SiteAccessInputSchema = z.object({
  gateCode: z.string().nullish(),
  hoaApprovalNeeded: z.boolean().nullish(),
  additionalInformation: z.string().nullish(),
});

export type SiteAccessInput = z.infer<typeof SiteAccessInputSchema>;

// Site access rides nested under `propertyAddress` on the wire; dropping it here would make Zod strip it
// silently from stale clients and queued partner payloads.
const UpdateWorksiteAddressSchema = UpdatePropertyAddressSchema.extend(SiteAccessInputSchema.shape);
const CreateWorksiteAddressSchema = CreatePropertyAddressSchema.extend(SiteAccessInputSchema.shape);

type CreateWorksiteAddress = z.infer<typeof CreateWorksiteAddressSchema>;

const UpdateWorksiteSchema = z.object({
  id: z.number(),
  bidId: z.number().optional(),
  propertyAddress: UpdateWorksiteAddressSchema.optional(),
  homeowner: UpdateHomeownerSchema.omit({ phones: true }).optional(),
  primaryPhone: z.string().optional(),
});

export type UpdateWorksite = z.infer<typeof UpdateWorksiteSchema>;

export const UpdateWorksiteDetailsSchema = z.object({
  propertyAddress: UpdatePropertyAddressSchema.optional(),
  homeowner: UpdateHomeownerSchema.optional(),
});

export const CreateWorksiteSchema = z.object({
  propertyAddress: CreateWorksiteAddressSchema,
  billingAddress: CreatePropertyAddressSchema.optional(),
  homeowner: CreateHomeownerSchema,
});

export type CreateWorksite = z.infer<typeof CreateWorksiteSchema>;

// What createWorksite stores, built field by field on the server so a client key or unit never reaches the row.
interface ServerSetAddressFields {
  key: string | null;
  keyScheme: AddressKeyScheme | null;
  unit?: string | null;
}

export interface CreateBillingAddressData extends CreatePropertyAddress, ServerSetAddressFields {}

export interface CreatePropertyAddressData extends CreateWorksiteAddress, ServerSetAddressFields {}

export interface CreateWorksiteData {
  propertyAddress: CreatePropertyAddressData;
  billingAddress?: CreateBillingAddressData;
  homeowner: CreateWorksite["homeowner"];
}

// Only the web app's bid and worksite forms can show the unit prompt, so partner contracts never carry the flag:
// false asks for the prompt, true stores the whole building, and absent keeps the automated behavior.
const ConfirmedWholeBuildingShape = { confirmedWholeBuilding: z.boolean().optional() };
export const StaffCreateWorksiteSchema = CreateWorksiteSchema.extend({
  propertyAddress: CreateWorksiteAddressSchema.extend(ConfirmedWholeBuildingShape),
});
export type StaffCreateWorksite = z.infer<typeof StaffCreateWorksiteSchema>;
export const StaffUpdateWorksiteSchema = UpdateWorksiteSchema.extend({
  propertyAddress: UpdateWorksiteAddressSchema.extend(ConfirmedWholeBuildingShape).optional(),
});
export type StaffUpdateWorksite = z.infer<typeof StaffUpdateWorksiteSchema>;
